import { AnalyticsService } from '@alison/analytics/client';
import { Button, FormFieldLabel } from '@alison/ui';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import type { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTRPC } from '@/lib/trpc.js';
import { AccountPicker } from './AccountPicker.js';
import { JOB_TITLES } from './constants.js';
import type { JobTitleId } from './types.js';

const inviteInputSx: SxProps<Theme> = {
  '& .MuiOutlinedInput-root': {
    height: 36,
    borderRadius: 1,
    fontSize: 13,
    bgcolor: 'background.paper',
    '& .MuiOutlinedInput-notchedOutline': {
      borderWidth: 1.5,
      borderColor: 'divider',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
    },
  },
  '& .MuiOutlinedInput-input': {
    py: 0,
    px: 2,
  },
};

const fieldColumnSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  border: 'none',
};

interface InviteFormProps {
  onClose: () => void;
  onInvited: (email: string) => void;
  onError: (message: string) => void;
}

export function InviteForm({ onClose, onInvited, onError }: InviteFormProps): ReactNode {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [jobTitleId, setJobTitleId] = useState<JobTitleId | ''>('');
  const [integrationIds, setIntegrationIds] = useState<number[]>([]);

  const createInviteMutation = useMutation(
    trpc.invites.create.mutationOptions({
      onSuccess: (_data, variables) => {
        void queryClient.invalidateQueries({
          queryKey: trpc.users.list.queryKey(),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.invites.list.queryKey(),
        });
        AnalyticsService.track('Invite - Sent', {
          invitedEmail: variables.email,
          role: variables.role,
          integrationCount: variables.integrationIds.length,
        });
        onInvited(variables.email);
      },
      onError: (error) => {
        onError(error.message);
      },
    }),
  );

  const trimmedEmail = email.trim();
  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
  const showEmailError = trimmedEmail.length > 0 && !emailIsValid;
  const canSubmit = fullName.trim().length > 0 && emailIsValid && integrationIds.length > 0;

  function onSubmit(): void {
    if (!canSubmit) return;
    createInviteMutation.mutate({
      email: trimmedEmail,
      name: fullName.trim(),
      job: jobTitleId === '' ? undefined : jobTitleId,
      role: 'user',
      integrationIds,
    });
  }

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        border: '1px solid',
        borderColor: 'border.light',
        borderRadius: 1.5,
        bgcolor: 'info.bg',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 150px 1.5fr',
          gap: 2,
        }}
      >
        <Box sx={fieldColumnSx} component="fieldset">
          <FormFieldLabel>Full name</FormFieldLabel>
          <TextField
            fullWidth
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            data-testid="input-invite-name"
            sx={inviteInputSx}
          />
        </Box>
        <Box sx={fieldColumnSx} component="fieldset">
          <FormFieldLabel>Email</FormFieldLabel>
          <TextField
            fullWidth
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={showEmailError}
            helperText={showEmailError ? 'Enter a valid email address' : undefined}
            data-testid="input-invite-email"
            sx={inviteInputSx}
          />
        </Box>
        <Box sx={fieldColumnSx}>
          <FormFieldLabel>Job title</FormFieldLabel>
          <TextField
            select
            fullWidth
            value={jobTitleId}
            onChange={(event) => setJobTitleId(event.target.value as JobTitleId)}
            slotProps={{ select: { displayEmpty: true } }}
            data-testid="select-invite-job-title"
            sx={inviteInputSx}
          >
            <MenuItem value="">
              <Box component="span">No job title</Box>
            </MenuItem>
            {JOB_TITLES.map((jobTitle) => (
              <MenuItem key={jobTitle.id} value={jobTitle.id}>
                {jobTitle.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={fieldColumnSx}>
          <FormFieldLabel>Integrations</FormFieldLabel>
          <AccountPicker selectedIntegrationIds={integrationIds} onChange={setIntegrationIds} />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="text" onClick={onClose} data-testid="button-invite-cancel">
          Cancel
        </Button>
        <Button
          variant="gradient"
          disabled={!canSubmit || createInviteMutation.isPending}
          onClick={onSubmit}
          data-testid="button-invite-submit"
          startIcon={<SendIcon sx={{ fontSize: 14 }} />}
        >
          Send invitation
        </Button>
      </Box>
    </Box>
  );
}
